// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.DefaultMapPropsMappingProps.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------
import { toJS } from 'mobx';

import { DEFAULT_SETTING_VALUE } from 'logic/data/Izenda.ReportPartMapConstants';
import { parser as chartDataParser } from 'share/utils/Izenda.ChartDataParser';
import { get, getOr, isEqual } from 'lodash/fp';

export default (reportPartDetails, fieldStore) => {
  const reportPartContent = reportPartDetails.reportPartContent || {};

  const getProperties = key => {
    if (!key) {
      const properties = reportPartDetails && reportPartDetails.getReportPartProperties();
      const props = toJS(properties);
      return props;
    }

    return reportPartContent.getReportPartProperties(key);
  };

  return {
    getAllChartProps() {
      var mapProperties = reportPartContent.getMapOptions();
      return {
        chartOptions: {
          ...mapProperties,
          colors: reportPartDetails.getThemeColors(mapProperties)
        },
        seriesOptions: reportPartDetails.fieldCollection,
        chartData: chartDataParser(reportPartContent.activeElmsByContainers, reportPartDetails.expandedLevel, {
          fieldsMapping: reportPartDetails.previewDataFieldsMapping,
          records: reportPartDetails.previewDataRecords,
          rawData: reportPartDetails.previewData
        })
      };
    },

    fnResetBorderDefaultValue: reportPartContent.resetBorderDefaultValue.bind(reportPartContent),
    fnResetBackgroundColorDefaultValue: reportPartContent.resetBackgroundColorDefaultValue.bind(reportPartContent),
    fnResetRefreshIntervalDefaultValue: reportPartContent.resetRefreshIntervalValue.bind(reportPartContent),
    fnResetLegendDefaultValue: reportPartContent.resetLegendDefaultValue.bind(reportPartContent),
    fnResetZoomDefaultValue: reportPartContent.resetZoomDefaultValue.bind(reportPartContent),

    getBorder() {
      return {
        'izEntire.borderColor': getProperties('izEntire.borderColor'),
        'izEntire.borderWidth': getProperties('izEntire.borderWidth'),
        'izEntire.borderStyle': getProperties('izEntire.borderStyle')
      };
    },

    hadSettingBorder() {
      const currentSetting = {
        'izEntire.borderColor': getProperties('izEntire.borderColor'),
        'izEntire.borderWidth': getProperties('izEntire.borderWidth'),
        'izEntire.borderStyle': getProperties('izEntire.borderStyle')
      };

      return !(
        isEqual(currentSetting, DEFAULT_SETTING_VALUE.borderDefault) ||
        isEqual(currentSetting, DEFAULT_SETTING_VALUE.borderNew)
      );
    },

    getBackgroundColor() {
      return {
        'izEntire.backgroundColor': getProperties('izEntire.backgroundColor'),
        'izPlot.backgroundColor': getProperties('izPlot.backgroundColor')
      };
    },

    hadSettingBackgroundColor() {
      const currentSetting = {
        'izEntire.backgroundColor': getProperties('izEntire.backgroundColor'),
        'izPlot.backgroundColor': getProperties('izPlot.backgroundColor')
      };

      return !(
        isEqual(currentSetting, DEFAULT_SETTING_VALUE.backGroundColorDefault) ||
        isEqual(currentSetting, DEFAULT_SETTING_VALUE.backGroundColorNew)
      );
    },

    getColorTheme() {
      const properties = getProperties() || {};
      return {
        colorPalette: properties.colorTheme,
        isDefaultTheme: properties.isDefaultTheme
      };
    },

    getZoomConfig() {
      return {
        'izMapNavigation.enabled': getProperties('izMapNavigation.enabled'),
        'izMapNavigation.align': getProperties('izMapNavigation.align'),
        'izMapNavigation.verticalAlign': getProperties('izMapNavigation.verticalAlign')
      };
    },

    hadSettingZoom() {
      return getProperties('izMapNavigation.enabled');
    },

    getLegendSettings() {
      return {
        'izLegend.visibility': getProperties('izLegend.visibility'),
        'izLegend.horizontalAlign': getProperties('izLegend.horizontalAlign'),
        'izLegend.verticalAlign': getProperties('izLegend.verticalAlign'),
        'izLegend.borderColor': getProperties('izLegend.borderColor'),
        'izLegend.borderWidth': getProperties('izLegend.borderWidth'),
        'izLegend.fontStyling': getProperties('izLegend.fontStyling'),
        'izLegend.title': getProperties('izLegend.title'),
        'izLegend.layout': getProperties('izLegend.layout'),
        'izLegend.backgroundColor': getProperties('izLegend.backgroundColor')
      };
    },

    hadSettingLegend() {
      return getProperties('izLegend.visibility');
    },

    getDataRefreshInterval() {
      return getProperties('dataRefreshInterval');
    },

    hadSettingRefreshInterval() {
      const defaultSetting = DEFAULT_SETTING_VALUE.refreshIntervalDefault;
      const refreshSetting = getProperties('dataRefreshInterval');

      if (!refreshSetting) return false;

      return (
        refreshSetting.value !== defaultSetting.value || refreshSetting.updateInterval !== defaultSetting.updateInterval
      );
    }
  };
};



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/ReportBody/ReportPartType/ReportPartMap/Izenda.DefaultMapPropsMappingProps.js